import React, {FC, useEffect, useState} from "react"
import {Grid} from "react-bootstrap"
import {RouteComponentProps} from "react-router-dom"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {DealBrand, DealBrandSearchResponse} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import dealBrandService from "@irati/deal-brands/services/dealBrands"
import tracking from "@irati-tracking/perks/perks"
import {useDealList} from "@irati/deals/hooks"
import DealBrandResults from "@irati-web/search/src/components/DealBrandResults"
import SearchBrands from "@irati/deals/apollo/query/SearchBrands"
import {Hr} from "@irati/ui/src/Layout"
import DealPages from "../../enums/DealPages"
import BrandBanner from "../../components/DealBrand/BrandBanner"
import FeedSlider from "../../components/FeedSlider"
import FPDealsSectionHeader from "../../components/DealBrand/FPDealsSectionHeader"

interface Props extends InjectIntl, RouteComponentProps<{dealBrandSlug: string}> {}

const DealBrandPage: FC<Props> = (props) => {
  const [dealBrand, setDealBrand] = useState<DealBrand>()
  const [load, setLoad] = useState<Promise<void>>()

  useEffect(() => {
    const getBrand = async (): Promise<void> => {
      const brand = await dealBrandService.getBySlug({slug: props.match.params.dealBrandSlug})
      setDealBrand(brand)
    }
    getBrand()

    setLoad(getBrand)
    tracking.viewBrand(dealBrand?.uuid!, dealBrand?.label!)
  }, [props.match.params.dealBrandSlug])

  const noFpDeals = useDealList({brandIds: [dealBrand?.uuid!], ttOnly: false, noTt: true, limit: 12}, {skip: !dealBrand})
  const fpDeals = useDealList({brandIds: [dealBrand?.uuid!], ttOnly: true, noTt: false, limit: 12}, {skip: !dealBrand})

  const fpDealListTagIds = fpDeals?.data?.results.map((el) => el.brandTagIds).flat()
  const noTtDealListTagIds = noFpDeals?.data?.results.map((el) => el.brandTagIds).flat()
  const allListTagIds = fpDealListTagIds?.concat(noTtDealListTagIds)
  const uniqBrandTagIds = Array.from(new Set(allListTagIds)).filter((n) => n)
  const variables = {
    brandTagIds: uniqBrandTagIds,
    excludeBrandId: dealBrand?.uuid
  }

  const allPerksCount = fpDeals.data?.total! + noFpDeals.data?.total!
  const showOtherPerksHeader = allPerksCount > noFpDeals.data?.results?.length!

  return (
    <Loading load={load} initLoad>
      <BrandBanner dealBrand={dealBrand!} totalResultsCount={allPerksCount} />
      <Grid>
        {!!fpDeals.data?.results.length && (
          <FeedSlider
            data={fpDeals.data?.results}
            SectionHeader={FPDealsSectionHeader}
            sectionBodyText={fpDeals.data?.results.length}
            page={DealPages.BRAND}
          />
        )}
        {!!noFpDeals.data?.results.length && (
          <FeedSlider
            data={noFpDeals.data?.results}
            sectionHeaderText={
              showOtherPerksHeader &&
              props.formatMessage({id: "perks.brand.brandPage.lane.moreDeals.title"}, {count: noFpDeals.data?.results.length})
            }
            page={DealPages.BRAND}
          />
        )}
        {variables.brandTagIds.length > 0 && variables.excludeBrandId && (
          <>
            <Hr marginTop={6} marginBottom={6} />
            <SearchBrands variables={variables}>
              {({data, error}) => {
                if (error || !data?.dealsBrands) return null

                let dealBrands
                if (data?.dealsBrands instanceof Array) {
                  dealBrands = data?.dealsBrands?.map((b: DealBrandSearchResponse) => b)
                }

                return <DealBrandResults dealBrands={dealBrands} wasAccessedFromRelevantBrands />
              }}
            </SearchBrands>
          </>
        )}
      </Grid>
    </Loading>
  )
}

export default injectIntl(DealBrandPage)
